/**
 * Box 3, "Metric เฉพาะ <channel>" (spec §3.3), and the GA4 tiles: one tile per
 * registry entry in the D1 scorecard grid (2 / 3 / 6 columns). A counted tile shows
 * its value and Δ with ▲▼ in its tone; an all-NULL tile reads "ไม่ได้รายงาน" (D1's word, rules-04 D4), never 0;
 * a conversion tile with nothing to count after the cut reads "—" (Review Focus 4).
 * A ratio tile under 500 impressions fades with "n<500", as box 2's cards do (rules-04 D1).
 * A tile that reads the Conversion objective alone under "all" says so, "CPL · Conversion", and judges its own rows
 * for n<500, as D1 box 3 does (rules-04 E4); with one objective selected CPA, CPL and ROAS name it, "CPA ·
 * Awareness" (rules-04 H1). One that reads "—" because the objective it names has no row in the page's scope says why
 * on a small line under its figure, "ไม่มีแคมเปญ Conversion ในช่วงนี้" (rules-04 H2).
 * Cut tiles carry the "ตัด 3 วันล่าสุด" chip while the cut is in force. A static tile
 * reads no data, so it has no Δ line and no data-delta. The platform's notes follow
 * the grid. data-value / data-delta hold the raw numbers ('' for null), placed as
 * ScorecardRow places them, so one live check reads boxes 2 and 3 alike.
 *
 * @module components/channels/PlatformTiles
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TONE_CLASS } from '@/services/looker/lookerDelta';
import { fmtDelta } from '@/services/overview/overviewFormat';
import { fmtTile, platformNotes, tileReason, tileTitle } from '@/services/channels/channelFormat';
import type { ChannelPlatform, PlatformTile } from '@/services/channels/channelTypes';

interface Props {
  platform: ChannelPlatform;
  tiles: PlatformTile[];
  currency: string | null;
  /** windows.cutApplied: the period ends after the cut. */
  cutApplied: boolean;
}

/** A figure; a word ("ไม่ได้รายงาน", "v1 ไม่แสดง") is set smaller so it never passes for a number. */
const FIGURE_CLASS = 'mt-1 break-words text-xl font-semibold tabular-nums text-ink-950';
const WORD_CLASS = 'mt-1 break-words text-sm font-medium text-neutral-700';
/** A ratio over fewer than 500 impressions, faded as box 2's cards fade (ScorecardRow, rules-04 D1). */
const LOW_SAMPLE_CLASS = 'mt-1 break-words text-xl font-semibold tabular-nums italic text-neutral-700';
/** D1's cut chip (ResultFootnotes, TopCampaignsTable). */
const CUT_CHIP_CLASS = 'inline-block whitespace-nowrap rounded-full border border-neutral-400 px-1.5 text-[11px] font-normal text-neutral-800';
/** Why a tile reads "—" (rules-04 H2): the second line under a figure, as D1's FIGURE_LINE_CLASS draws it. */
const REASON_CLASS = 'block text-xs font-normal not-italic text-neutral-700';

const Tile: React.FC<{ tile: PlatformTile; currency: string | null; cutChip: boolean }> = ({ tile, currency, cutChip }) => {
  const { t } = useTranslation();
  const worded = tile.state === 'notReported' || tile.state === 'notShown';
  const reason = tileReason(tile, t);
  return (
    <div data-metric={tile.metric} data-state={tile.state} className="min-w-0 rounded-xl border border-neutral-200 bg-white p-3">
      <dt className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-neutral-700">
        {tileTitle(tile, t)}
        {cutChip && <span className={CUT_CHIP_CLASS}>{t('overview.cutChip')}</span>}
      </dt>
      <dd className={worded ? WORD_CLASS : tile.lowSample ? LOW_SAMPLE_CLASS : FIGURE_CLASS}>
        <span data-value={tile.value === null ? '' : String(tile.value)}>{fmtTile(tile, currency, t)}</span>
        {tile.lowSample && <span className="ml-1 text-xs font-normal">{t('overview.lowSample')}</span>}
        {reason !== null && <span data-reason className={REASON_CLASS}>{reason}</span>}
      </dd>
      {!tile.isStatic && (
        <dd data-delta={tile.delta === null ? '' : String(tile.delta)} className={`mt-1 text-xs font-semibold tabular-nums ${TONE_CLASS[tile.tone]}`}>
          {fmtDelta(tile.delta)}
        </dd>
      )}
    </div>
  );
};

export const PlatformTiles: React.FC<Props> = ({ platform, tiles, currency, cutApplied }) => {
  const { t } = useTranslation();
  const notes = platformNotes(platform, t);
  return (
    <div data-platform-tiles={platform}>
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {tiles.map((tile) => <Tile key={tile.metric} tile={tile} currency={currency} cutChip={tile.cut && cutApplied} />)}
      </dl>
      {notes.length > 0 && (
        <ul data-platform-notes className="mt-3 list-disc space-y-1 pl-5 text-xs text-neutral-700">
          {notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}
    </div>
  );
};
